import { useState, useEffect } from 'react';
import type { PrintChallanData } from '@/utils/printDocument';

interface PrintChallanProps {
  data: PrintChallanData;
  onClose: () => void;
  onPrint: () => void;
}

interface Company {
  id: string;
  name: string;
  address: string;
  gstin: string;
  pan: string;
  phone: string;
  email: string;
  logo_url?: string | null;
}

export default function PrintChallan({
  data,
  onClose,
  onPrint,
}: PrintChallanProps) {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch company details
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/company/get`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        const result = await response.json();
        if (result?.success && result?.data) {
          setCompany(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch company:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  // ✅ Default fallback values
  const c = {
    name: company?.name || 'Company Name',
    address: company?.address || '',
    gstin: company?.gstin || '',
    pan: company?.pan || '',
    phone: company?.phone || '',
    email: company?.email || '',
    logo_url: company?.logo_url || null,
  };

  // ✅ Logo handling
  const companyLogo = c.logo_url || null;
  let logoUrl = null;
  if (companyLogo) {
    if (companyLogo.startsWith('data:image')) {
      logoUrl = companyLogo;
    } else {
      logoUrl = `${import.meta.env.VITE_API_URL}${companyLogo}`;
    }
  }

  // ✅ Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 flex items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-[#4f46e5] text-2xl" />
          <span className="text-sm text-slate-600">
            Loading company details...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8"
      onClick={onClose}
    >
      <div
        className="bg-white w-[210mm] shadow-2xl rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 bg-[#1e293b] text-white print:hidden">
          <div className="flex items-center gap-2">
            <i className="ri-truck-line text-indigo-300" />
            <span className="text-sm font-medium">
              Delivery Challan Preview — {data.challanNo}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onPrint}
              className="flex items-center gap-1.5 h-7 px-3 rounded text-xs font-medium bg-[#4f46e5] hover:bg-indigo-600 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-printer-line" /> Print
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/10 cursor-pointer text-slate-300"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {/* A4 Content */}
        <div className="p-8 text-[11px] print-area">
          {/* Header with Logo */}
          <div className="flex justify-between items-start mb-5 pb-4 border-b-2 border-[#e2e8f0]">
            <div className="flex items-start gap-4">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={c.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#4f46e5] bg-white shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-2xl font-bold text-[#4f46e5] border-2 border-[#4f46e5] shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold text-[#1e293b]">{c.name}</h1>
                <p className="text-[#64748b] mt-1 leading-relaxed">
                  {c.address}
                </p>
                <p className="mt-1">
                  <strong>GSTIN:</strong> {c.gstin} &nbsp;|&nbsp;{' '}
                  <strong>PAN:</strong> {c.pan} &nbsp;|&nbsp;{' '}
                  <strong>Ph:</strong> {c.phone}
                </p>
                <p>
                  <strong>Email:</strong> {c.email}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-extrabold text-[#4f46e5] tracking-wide">
                DELIVERY CHALLAN
              </p>
              <div className="mt-2 border border-[#e2e8f0] rounded-lg p-3 text-left inline-block">
                <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide">
                  Challan No
                </p>
                <p className="font-bold text-base text-[#1e293b]">
                  {data.challanNo}
                </p>
                <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide mt-2">
                  Date
                </p>
                <p>{data.date}</p>
                {data.vehicleNo && (
                  <>
                    <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide mt-2">
                      Vehicle No
                    </p>
                    <p>{data.vehicleNo}</p>
                  </>
                )}
                {data.lrNo && (
                  <>
                    <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide mt-2">
                      LR No
                    </p>
                    <p>{data.lrNo}</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Consignee Details */}
          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-[#f8fafc] rounded-lg">
            <div>
              <p className="text-[10px] text-[#94a3b8] uppercase font-semibold mb-1">
                Consignee (To)
              </p>
              <p className="font-bold text-sm text-[#1e293b]">
                {data.customerName}
              </p>
              <p className="text-[#64748b] mt-0.5 leading-relaxed">
                {data.billingAddress}
              </p>
            </div>
            {data.driverName && (
              <div>
                <p className="text-[10px] text-[#94a3b8] uppercase font-semibold mb-1">
                  Driver Details
                </p>
                <p className="font-bold text-sm text-[#1e293b]">
                  {data.driverName}
                </p>
                {data.vehicleNo && (
                  <p className="text-[#64748b] mt-0.5">
                    Vehicle: {data.vehicleNo}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Items Table */}
          <table className="w-full border-collapse border border-[#e2e8f0] text-[10px] mb-6">
            <thead>
              <tr className="bg-[#f8fafc]">
                <th
                  className="border border-[#e2e8f0] px-2 py-1.5 font-semibold text-[#64748b] uppercase text-center"
                  style={{ width: '30px' }}
                >
                  Sr
                </th>
                <th className="border border-[#e2e8f0] px-2 py-1.5 font-semibold text-[#64748b] uppercase text-left">
                  Description
                </th>
                <th className="border border-[#e2e8f0] px-2 py-1.5 font-semibold text-[#64748b] uppercase text-right">
                  Qty
                </th>
                <th className="border border-[#e2e8f0] px-2 py-1.5 font-semibold text-[#64748b] uppercase text-left">
                  Unit
                </th>
                {data.items.some((i) => i.rate) && (
                  <>
                    <th className="border border-[#e2e8f0] px-2 py-1.5 font-semibold text-[#64748b] uppercase text-right">
                      Rate
                    </th>
                    <th className="border border-[#e2e8f0] px-2 py-1.5 font-semibold text-[#64748b] uppercase text-right">
                      Amount
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, i) => (
                <tr key={i} className={i % 2 === 1 ? 'bg-[#fafafa]' : ''}>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-center">
                    {item.sr}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 font-medium">
                    {item.name}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {item.qty}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5">
                    {item.unit}
                  </td>
                  {data.items.some((i) => i.rate) && (
                    <>
                      <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                        {item.rate ? `₹${item.rate.toFixed(2)}` : '—'}
                      </td>
                      <td className="border border-[#e2e8f0] px-2 py-1.5 text-right font-bold">
                        {item.amount ? `₹${item.amount.toFixed(2)}` : '—'}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
            {data.items.some((i) => i.amount) && (
              <tfoot>
                <tr className="bg-[#f8fafc] font-bold">
                  <td
                    colSpan={5}
                    className="border border-[#e2e8f0] px-2 py-1.5 text-right"
                  >
                    Total
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    ₹
                    {data.items
                      .reduce((s, i) => s + (i.amount || 0), 0)
                      .toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-4 mt-8 pt-4 border-t border-[#e2e8f0]">
            <div className="text-center text-[10px] text-[#64748b]">
              <p>Receiver's Signature &amp; Stamp</p>
              <p className="mt-8 border-t border-[#e2e8f0] pt-1">
                Date: ________________
              </p>
            </div>
            <div className="text-center text-[10px] text-[#64748b]">
              <p className="font-semibold text-[#1e293b]">{c.name}</p>
              <p className="mt-8 border-t border-[#e2e8f0] pt-1">
                Authorised Signatory
              </p>
            </div>
          </div>

          <p className="text-center text-[9px] text-[#94a3b8] italic mt-4">
            This is a computer generated delivery challan.
          </p>
        </div>
      </div>
    </div>
  );
}
